import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import { useTranslation } from 'react-i18next'
import { ReferralStatus } from '../../utils/referral'
import { getReferralStatus } from '../../redux/actions/referralAction'
import { REFER } from '../../routes/routeConstant'
import './invite-friends.css'

interface Props {
  /** Where the card is shown, for PostHog. */
  placement: 'settings_plan' | 'payment_successful'
}

const capture = (event: string, props: Record<string, any>) => {
  try {
    posthog.capture(event, props)
  } catch {
    /* analytics must never break the page */
  }
}

/**
 * "Give 2 weeks, get 2 weeks": a one-line invitation that opens /refer, where the link, the share
 * buttons and the invite tracker live. Renders nothing for anyone the backend says cannot refer
 * (Free, lapsed, App Store billed), so it can be dropped on any page.
 */
const InviteFriendsCard: React.FC<Props> = ({ placement }) => {
  const { t } = useTranslation('referral')
  const navigate = useNavigate()
  const [status, setStatus] = useState<ReferralStatus | null>(null)
  const viewed = useRef(false)

  useEffect(() => {
    let alive = true
    getReferralStatus().then((s) => alive && setStatus(s))
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    if (!status?.canRefer || viewed.current) return
    viewed.current = true
    capture('referral_card_viewed', { placement, rewards_this_year: status.rewardsThisYear })
  }, [status, placement])

  if (!status?.canRefer || !status.link) return null

  return (
    <section className='invite-friends' aria-labelledby='invite-friends-title'>
      <span className='invite-friends-icon' aria-hidden='true'>
        <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <path d='M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z' />
        </svg>
      </span>
      <div className='invite-friends-text'>
        <h3 id='invite-friends-title' className='invite-friends-title'>
          {status.earnsReward === false ? t('inviteCard.giftTitle') : t('inviteCard.rewardTitle')}
        </h3>
        <p className='invite-friends-body'>
          {status.earnsReward === false
            ? t('inviteCard.giftBody')
            : t('inviteCard.rewardBody', { days: status.friendTrialDays })}
        </p>
      </div>
      <button
        type='button'
        className='invite-friends-btn'
        onClick={() => {
          capture('referral_card_clicked', { placement })
          navigate(REFER)
        }}
      >
        {t('inviteCard.cta')}
      </button>
    </section>
  )
}

export default InviteFriendsCard
